/**
 * Auth-client — praat met de /api/auth/... en /api/access/... routes op
 * dezelfde Cloudflare Worker als de Live Session (zie useLiveSession.js voor
 * dezelfde host-conventie: REACT_APP_PARTY_HOST, met localhost:8787 als
 * fallback tijdens lokaal ontwikkelen via `npx wrangler dev`).
 *
 * Bewaart alleen de ondertekende identity-token + user-info in localStorage
 * (geen wachtwoorden, nooit). "Per sessie goedkeuren": de token bewijst wie
 * je bent, maar geeft op zichzelf geen toegang tot de Chart — die status
 * wordt steeds opnieuw gecheckt via /api/access/status.
 */

const PARTY_HOST = process.env.REACT_APP_PARTY_HOST || "127.0.0.1:8787";
const API_BASE = `${window.location.protocol === "https:" ? "https" : "http"}://${PARTY_HOST}`;

const STORAGE_KEY = "wyckoffcafe.auth";

function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveSession(session) {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* localStorage kan geblokkeerd zijn — negeer, sessie leeft dan alleen in memory */
  }
}

async function request(path, { method = "GET", body, token } = {}) {
  const headers = {};
  if (body) headers["content-type"] = "application/json";
  if (token) headers["authorization"] = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error("Cannot reach the server. Check your internet connection.");
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || "Something went wrong.");
  }
  return data;
}

export async function register({ username, password, name }) {
  return request("/api/auth/register", { method: "POST", body: { username, password, name } });
}

export async function login({ username, password }) {
  const data = await request("/api/auth/login", { method: "POST", body: { username, password } });
  saveSession({ token: data.token, user: data.user });
  return data.user;
}

export function logout() {
  saveSession(null);
}

export function getSession() {
  return loadSession();
}

export async function requestAccess() {
  const session = loadSession();
  if (!session) throw new Error("Not signed in.");
  return request("/api/access/request", { method: "POST", token: session.token });
}

export async function getAccessStatus() {
  const session = loadSession();
  if (!session) throw new Error("Not signed in.");
  return request("/api/access/status", { token: session.token });
}

export async function getPendingRequests() {
  const session = loadSession();
  if (!session) throw new Error("Not signed in.");
  return request("/api/access/pending", { token: session.token });
}

export async function decideRequest(requestId, decision) {
  const session = loadSession();
  if (!session) throw new Error("Not signed in.");
  return request("/api/access/decide", { method: "POST", token: session.token, body: { requestId, decision } });
}

// ── Lidmaatschap (Stripe) + Discord ──────────────────────────────
// Beide geven een URL terug waar de browser naartoe moet (Stripe Checkout /
// Customer Portal / Discord-autorisatie); we sturen de gebruiker er direct heen.

async function goTo(path, body) {
  const session = loadSession();
  if (!session) throw new Error("Not signed in.");
  const { url } = await request(path, { method: "POST", token: session.token, body: body || {} });
  window.location.href = url;
}

export function startCheckout() {
  return goTo("/api/billing/checkout");
}

export function openBillingPortal() {
  return goTo("/api/billing/portal");
}

export function linkDiscord() {
  return goTo("/api/discord/link");
}

export async function unlinkDiscord() {
  const session = loadSession();
  if (!session) throw new Error("Not signed in.");
  return request("/api/discord/unlink", { method: "POST", token: session.token });
}

// ── Coach: member overview ───────────────────────────────────────
export async function getMembers() {
  const session = loadSession();
  if (!session) throw new Error("Not signed in.");
  return request("/api/admin/members", { token: session.token });
}

export async function setCompAccess(userId, comp) {
  const session = loadSession();
  if (!session) throw new Error("Not signed in.");
  return request("/api/admin/comp", { method: "POST", token: session.token, body: { userId, comp } });
}

export async function endMembership(userId) {
  const session = loadSession();
  if (!session) throw new Error("Not signed in.");
  return request("/api/admin/end-membership", { method: "POST", token: session.token, body: { userId } });
}
